import React, { useState, useRef, useCallback } from 'react';

interface TranslationData {
    title_es: string;
    objective_es: string;
}

interface TranslationTooltipProps {
    /** Original english title to translate */
    title: string;
    /** Original english objective to translate */
    objective: string;
    /** The element to wrap with the tooltip trigger */
    children: React.ReactNode;
}

// Module-level cache: key = title, value = TranslationData
const translationCache = new Map<string, TranslationData>();

const TranslationTooltip: React.FC<TranslationTooltipProps> = ({ title, objective, children }) => {
    const [tooltip, setTooltip] = useState<TranslationData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [visible, setVisible] = useState(false);
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchTranslation = useCallback(async () => {
        const cacheKey = title;

        // Serve from cache if available
        if (translationCache.has(cacheKey)) {
            setTooltip(translationCache.get(cacheKey)!);
            setVisible(true);
            return;
        }

        setIsLoading(true);
        setError(false);
        setVisible(true);

        try {
            const response = await fetch('/api/spec-review/translate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ title, objective })
            });

            if (!response.ok) throw new Error('Translation failed');

            const data: TranslationData = await response.json();
            translationCache.set(cacheKey, data);
            setTooltip(data);
        } catch {
            setError(true);
        } finally {
            setIsLoading(false);
        }
    }, [title, objective]);

    const handleMouseEnter = () => {
        // 400ms delay to avoid firing on quick mouse passes
        hoverTimer.current = setTimeout(() => {
            fetchTranslation();
        }, 400);
    };

    const handleMouseLeave = () => {
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
            hoverTimer.current = null;
        }
        setVisible(false);
    };

    return (
        <div
            className="relative inline-block w-full"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {children}

            {/* Tooltip */}
            {visible && (
                <div
                    className="
            absolute left-0 top-full mt-2 z-50 w-80
            bg-slate-900 border border-slate-700/80 rounded-xl shadow-2xl
            p-4 text-sm
            animate-in fade-in slide-in-from-top-1 duration-150
          "
                    onMouseEnter={() => {
                        // Keep visible if user moves mouse into tooltip
                        if (hoverTimer.current) clearTimeout(hoverTimer.current);
                    }}
                >
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700/60">
                        <span className="text-base">🌐</span>
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                            Traducción al Español
                        </span>
                    </div>

                    {isLoading && (
                        <div className="flex items-center gap-2 text-slate-400">
                            <div className="w-3 h-3 rounded-full border-2 border-slate-600 border-t-blue-400 animate-spin" />
                            <span className="text-xs">Traduciendo...</span>
                        </div>
                    )}

                    {error && !isLoading && (
                        <p className="text-xs text-red-400">
                            No se pudo traducir. Intenta de nuevo.
                        </p>
                    )}

                    {tooltip && !isLoading && (
                        <div className="space-y-3">
                            <div>
                                <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1 font-medium">Título</p>
                                <p className="text-white font-medium leading-snug">{tooltip.title_es}</p>
                            </div>
                            <div>
                                <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1 font-medium">Objetivo</p>
                                <p className="text-slate-300 text-xs leading-relaxed">{tooltip.objective_es}</p>
                            </div>
                            <div className="flex items-center gap-1 pt-1 border-t border-slate-700/40">
                                <span className="text-[10px] text-slate-600">✨ gpt-4o-mini · es-CL</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TranslationTooltip;
